import React from "react";

const SHIELD_PATH =
  "M14 10 L48 2 L82 10 V40 C82 64 67 80 48 88 C29 80 14 64 14 40 Z";

export type ShieldBeat = "handshake" | "power-on" | "ambient" | "static" | "data-flow";

type Trace = { d: string; edge: "U" | "D" | "L" | "R"; ringIndex: number };

function buildTraces(): Trace[] {
  const w = 28;
  const cx = 48;
  const cy = 48;
  const x0 = cx - w / 2;
  const y0 = cy - w / 2;
  const x1 = cx + w / 2;
  const y1 = cy + w / 2;
  const pinOffsets = [-11.5, -7, -2.3, 2.3, 7, 11.5];
  const pinStub = 2.5;
  const escapeBend = 6;
  const rimReach = 22;
  const rimReachUp = 34;
  const ringFor = (idx: number, total: number) => {
    const mid = (total - 1) / 2;
    return Math.round(Math.abs(idx - mid));
  };
  const traces: Trace[] = [];
  pinOffsets.forEach((o, i) => {
    const sign = Math.sign(o) || 1;
    const ring = ringFor(i, pinOffsets.length);
    {
      const xs = cx + o;
      const yPinEnd = y0 - pinStub;
      const yBendEnd = yPinEnd - escapeBend;
      const xBendEnd = xs + sign * escapeBend;
      const yEnd = y0 - rimReachUp;
      traces.push({ d: `M${xs} ${y0} L${xs} ${yPinEnd} L${xBendEnd} ${yBendEnd} L${xBendEnd} ${yEnd}`, edge: "U", ringIndex: ring });
    }
    {
      const xs = cx + o;
      const yPinEnd = y1 + pinStub;
      const yBendEnd = yPinEnd + escapeBend;
      const xBendEnd = xs + sign * escapeBend;
      const yEnd = y1 + rimReach;
      traces.push({ d: `M${xs} ${y1} L${xs} ${yPinEnd} L${xBendEnd} ${yBendEnd} L${xBendEnd} ${yEnd}`, edge: "D", ringIndex: ring });
    }
    {
      const ys = cy + o;
      const xPinEnd = x0 - pinStub;
      const xBendEnd = xPinEnd - escapeBend;
      const yBendEnd = ys + sign * escapeBend;
      const xEnd = x0 - rimReach;
      traces.push({ d: `M${x0} ${ys} L${xPinEnd} ${ys} L${xBendEnd} ${yBendEnd} L${xEnd} ${yBendEnd}`, edge: "L", ringIndex: ring });
    }
    {
      const ys = cy + o;
      const xPinEnd = x1 + pinStub;
      const xBendEnd = xPinEnd + escapeBend;
      const yBendEnd = ys + sign * escapeBend;
      const xEnd = x1 + rimReach;
      traces.push({ d: `M${x1} ${ys} L${xPinEnd} ${ys} L${xBendEnd} ${yBendEnd} L${xEnd} ${yBendEnd}`, edge: "R", ringIndex: ring });
    }
  });
  return traces;
}

function buildPinStubs() {
  const w = 28;
  const cx = 48;
  const cy = 48;
  const x0 = cx - w / 2;
  const y0 = cy - w / 2;
  const x1 = cx + w / 2;
  const y1 = cy + w / 2;
  const pinStub = 2.5;
  const offsets = [-11.5, -7, -2.3, 2.3, 7, 11.5];
  const stubs: { x: number; y: number; w: number; h: number }[] = [];
  offsets.forEach((o) => {
    stubs.push({ x: cx + o - 0.7, y: y0 - pinStub, w: 1.4, h: pinStub });
    stubs.push({ x: cx + o - 0.7, y: y1, w: 1.4, h: pinStub });
    stubs.push({ x: x0 - pinStub, y: cy + o - 0.7, w: pinStub, h: 1.4 });
    stubs.push({ x: x1, y: cy + o - 0.7, w: pinStub, h: 1.4 });
  });
  return stubs;
}

const TRACES = buildTraces();
const PIN_STUBS = buildPinStubs();

type Pt = [number, number];

function tracePoints(d: string): { chip: Pt; pinEnd: Pt; bend: Pt; rim: Pt } {
  const n = d.match(/-?\d+(?:\.\d+)?/g)!.map(Number);
  return {
    chip: [n[0], n[1]],
    pinEnd: [n[2], n[3]],
    bend: [n[4], n[5]],
    rim: [n[6], n[7]],
  };
}

const dist = (a: Pt, b: Pt) => Math.hypot(a[0] - b[0], a[1] - b[1]);
const lerp = (a: Pt, b: Pt, t: number): Pt => [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t];

const FLOW_CYCLE_MS = 4000;
const FLOW_PACKET_COUNT = 8;
const FLOW_STAGGER_MS = FLOW_CYCLE_MS / FLOW_PACKET_COUNT;
const FLOW_ARRIVAL_PCT = 0.78;
const FLOW_FIRST_ARRIVAL_MS = Math.round(FLOW_CYCLE_MS * FLOW_ARRIVAL_PCT);
const FLOW_LABELS_DEFAULT = ["BSB", "ACCT", "EMAIL", "PHONE", "ADDR", "ID#", "ABN", "NAME"];
const FLOW_SCRAMBLE_GLYPHS: ReadonlyArray<readonly [string, string, string]> = [
  ["9F·2", "7C•A", "A1#4"],
  ["3B·D", "F2•1", "5E#7"],
  ["A4·F", "1B•9", "C7#2"],
  ["6D·8", "E0•3", "B5#1"],
  ["2F·A", "8C•E", "D3#6"],
  ["5A·1", "9E•7", "4B#F"],
  ["C8·3", "2A•5", "F0#9"],
  ["7E·B", "1D•A", "3C#8"],
];
const FLOW_CIPHER_GLYPH = "••••";

type FlowPacket = {
  traceIndex: number;
  delayMs: number;
  chip: Pt;
  pinEnd: Pt;
  bend: Pt;
  rim: Pt;
};

const FLOW_PACKETS: FlowPacket[] = (() => {
  const byEdge: Record<Trace["edge"], number[]> = { U: [], D: [], L: [], R: [] };
  TRACES.forEach((t, i) => byEdge[t.edge].push(i));
  const order: Trace["edge"][] = ["U", "R", "D", "L"];
  const picks: number[] = [];
  order.forEach((e) => {
    const arr = byEdge[e];
    picks.push(arr[1], arr[4]);
  });
  return picks.map((traceIndex, k) => {
    const tp = tracePoints(TRACES[traceIndex].d);
    return {
      traceIndex,
      delayMs: Math.round(k * FLOW_STAGGER_MS),
      ...tp,
    };
  });
})();

export interface AnimatedShieldProps {
  beat?: ShieldBeat;
  tone?: string;
  cut?: string;
  glow?: string;
  className?: string;
  dataLabels?: string[];
  variant?: "filled" | "outline";
}

export function AnimatedShield({
  beat = "static",
  tone = "hsl(var(--brand-forest))",
  cut = "hsl(var(--brand-sand))",
  glow = "hsl(var(--brand-brass))",
  className,
  dataLabels,
  variant = "filled",
}: AnimatedShieldProps) {
  const id = React.useId();
  const safeId = "s" + id.replace(/[:]/g, "");
  const w = 28;
  const cx = 48;
  const cy = 48;
  const x0 = cx - w / 2;
  const y0 = cy - w / 2;
  const cycle = 2800;

  const labels = dataLabels && dataLabels.length > 0 ? dataLabels : FLOW_LABELS_DEFAULT;

  const [packetLabelIdx, setPacketLabelIdx] = React.useState<number[]>(() =>
    Array.from({ length: FLOW_PACKETS.length }, (_, i) => i % Math.max(labels.length, 1))
  );
  const packetRefs = React.useRef<Array<SVGGElement | null>>([]);

  React.useEffect(() => {
    if (beat !== "data-flow") return;
    const cleanups: Array<() => void> = [];
    packetRefs.current.forEach((el, i) => {
      if (!el) return;
      const onIter = () => {
        setPacketLabelIdx((prev) => {
          const next = prev.slice();
          next[i] = (next[i] + 1) % Math.max(labels.length, 1);
          return next;
        });
      };
      el.addEventListener("animationiteration", onIter);
      cleanups.push(() => el.removeEventListener("animationiteration", onIter));
    });
    return () => cleanups.forEach((c) => c());
  }, [beat, labels.length]);

  const flowPacketKeyframes = beat === "data-flow"
    ? FLOW_PACKETS.map((p, i) => {
        const d1 = dist(p.rim, p.bend);
        const d2 = dist(p.bend, p.pinEnd);
        const d3 = dist(p.pinEnd, p.chip);
        const total = d1 + d2 + d3;
        const arrival = FLOW_ARRIVAL_PCT * 100;
        const tBend = total > 0 ? (d1 / total) * arrival : arrival * 0.7;
        const tPinEnd = total > 0 ? ((d1 + d2) / total) * arrival : arrival * 0.93;
        const tFadeIn = tBend * 0.5;
        const fadeInPos = lerp(p.rim, p.bend, 0.5);
        const W = Math.max(tPinEnd - tBend, 0.01);
        const slot = W / 6;
        // Six sub-slots across scramble window. Each scramble layer owns 2 non-adjacent
        // slots so the three layers cycle 1→2→3→1→2→3 — reads as rapid flicker.
        const sub = (k: number) => tBend + slot * k;
        const eps = Math.min(0.4, slot * 0.15);
        const scrambleKeyframes = (layerIdx: 0 | 1 | 2) => {
          const a0 = sub(layerIdx);
          const a1 = sub(layerIdx + 1);
          const b0 = sub(layerIdx + 3);
          const b1 = sub(layerIdx + 4);
          return `
      @keyframes ${safeId}-flow-text-s${layerIdx + 1}-${i} {
        0%, ${a0.toFixed(2)}% { opacity: 0; }
        ${(a0 + eps).toFixed(2)}%, ${(a1 - eps).toFixed(2)}% { opacity: 1; }
        ${a1.toFixed(2)}%, ${b0.toFixed(2)}% { opacity: 0; }
        ${(b0 + eps).toFixed(2)}%, ${(b1 - eps).toFixed(2)}% { opacity: 1; }
        ${b1.toFixed(2)}%, 100% { opacity: 0; }
      }
      .${safeId}-flow-text-s${layerIdx + 1}-${i} {
        animation: ${safeId}-flow-text-s${layerIdx + 1}-${i} ${FLOW_CYCLE_MS}ms linear ${p.delayMs}ms infinite;
      }
    `;
        };
        return `
      @keyframes ${safeId}-flow-${i} {
        0%   { transform: translate(${p.rim[0]}px, ${p.rim[1]}px) scale(1); opacity: 0; }
        ${tFadeIn.toFixed(2)}%  { transform: translate(${fadeInPos[0].toFixed(2)}px, ${fadeInPos[1].toFixed(2)}px) scale(1); opacity: 1; }
        ${tBend.toFixed(2)}%  { transform: translate(${p.bend[0]}px, ${p.bend[1]}px) scale(1); opacity: 1; }
        ${tPinEnd.toFixed(2)}%  { transform: translate(${p.pinEnd[0]}px, ${p.pinEnd[1]}px) scale(1); opacity: 1; }
        ${arrival.toFixed(2)}%  { transform: translate(${p.chip[0]}px, ${p.chip[1]}px) scale(1); opacity: 1; }
        88%  { transform: translate(${p.chip[0]}px, ${p.chip[1]}px) scale(0.18); opacity: 0.7; }
        100% { transform: translate(${p.chip[0]}px, ${p.chip[1]}px) scale(0.05); opacity: 0; }
      }
      .${safeId}-flow-packet-${i} {
        animation: ${safeId}-flow-${i} ${FLOW_CYCLE_MS}ms linear ${p.delayMs}ms infinite;
      }
      @keyframes ${safeId}-flow-text-plain-${i} {
        0%, ${(tBend - eps).toFixed(2)}% { opacity: 1; }
        ${tBend.toFixed(2)}%, 100% { opacity: 0; }
      }
      .${safeId}-flow-text-plain-${i} {
        animation: ${safeId}-flow-text-plain-${i} ${FLOW_CYCLE_MS}ms linear ${p.delayMs}ms infinite;
      }
      ${scrambleKeyframes(0)}
      ${scrambleKeyframes(1)}
      ${scrambleKeyframes(2)}
      @keyframes ${safeId}-flow-text-cipher-${i} {
        0%, ${tPinEnd.toFixed(2)}% { opacity: 0; }
        ${(tPinEnd + eps).toFixed(2)}%, 100% { opacity: 1; }
      }
      .${safeId}-flow-text-cipher-${i} {
        animation: ${safeId}-flow-text-cipher-${i} ${FLOW_CYCLE_MS}ms linear ${p.delayMs}ms infinite;
      }
      @keyframes ${safeId}-flow-lit-${i} {
        0%, ${tBend.toFixed(2)}% { stroke-width: 0.35; }
        ${(tBend + eps).toFixed(2)}%, ${(tPinEnd - eps).toFixed(2)}% { stroke-width: 0.75; }
        ${tPinEnd.toFixed(2)}%, 100% { stroke-width: 0.35; }
      }
      .${safeId}-flow-lit-${i} {
        animation: ${safeId}-flow-lit-${i} ${FLOW_CYCLE_MS}ms linear ${p.delayMs}ms infinite;
      }
    `;
      }).join("")
    : "";

  const css = `
    .${safeId}-trace-base { stroke: ${cut}; stroke-width: 0.85; fill: none; }
    .${safeId}-pin { fill: ${cut}; }
    .${safeId}-frame { stroke: ${cut}; stroke-width: 2.4; fill: none; }
    .${safeId}-die { fill: ${cut}; transform-origin: ${cx}px ${cy}px; }
    .${safeId}-glow { stroke: ${glow}; stroke-width: 1.2; fill: none; stroke-linecap: round; }
    .${safeId}-handshake-glow {
      stroke-dasharray: 18 82; stroke-dashoffset: 100; opacity: 0;
      animation: ${safeId}-hs ${cycle}ms ease-in-out infinite;
    }
    @keyframes ${safeId}-hs {
      0%   { stroke-dashoffset: 100; opacity: 0; }
      6%   { opacity: 1; }
      32%  { stroke-dashoffset: 0;   opacity: 1; }
      36%  { opacity: 0; }
      42%  { stroke-dashoffset: 0;   opacity: 0; }
      48%  { stroke-dashoffset: 0;   opacity: 1; }
      78%  { stroke-dashoffset: -100; opacity: 1; }
      84%  { opacity: 0; }
      100% { stroke-dashoffset: -100; opacity: 0; }
    }
    .${safeId}-handshake-die { animation: ${safeId}-die-flash ${cycle}ms ease-in-out infinite; }
    .${safeId}-handshake-frame { animation: ${safeId}-frame-flash ${cycle}ms ease-in-out infinite; }
    @keyframes ${safeId}-die-flash {
      0%, 34%   { fill: ${cut}; transform: scale(1); }
      40%, 44%  { fill: ${glow}; transform: scale(1.12); }
      50%, 100% { fill: ${cut}; transform: scale(1); }
    }
    @keyframes ${safeId}-frame-flash {
      0%, 36%   { stroke: ${cut}; }
      42%, 46%  { stroke: ${glow}; }
      52%, 100% { stroke: ${cut}; }
    }
    .${safeId}-poweron-frame { transform-origin: ${cx}px ${cy}px; animation: ${safeId}-loop-frame 5000ms ease-in-out infinite; }
    .${safeId}-poweron-die { transform-origin: ${cx}px ${cy}px; animation: ${safeId}-loop-die 5000ms ease-in-out infinite; }
    .${safeId}-poweron-pin { animation: ${safeId}-loop-pin 5000ms ease-in-out infinite; }
    .${safeId}-poweron-trace { stroke-dasharray: 100; animation: ${safeId}-loop-trace 5000ms ease-in-out infinite; }
    @keyframes ${safeId}-loop-frame {
      0%, 4%   { transform: scale(0.6); opacity: 0; }
      14%      { transform: scale(1.05); opacity: 1; }
      18%, 94% { transform: scale(1); opacity: 1; }
      100%     { transform: scale(1); opacity: 0; }
    }
    @keyframes ${safeId}-loop-die {
      0%, 14%  { transform: scale(0); opacity: 0; }
      22%      { transform: scale(1.15); opacity: 1; }
      26%, 94% { transform: scale(1); opacity: 1; }
      100%     { transform: scale(1); opacity: 0; }
    }
    @keyframes ${safeId}-loop-pin {
      0%, 22%  { opacity: 0; }
      28%, 94% { opacity: 1; }
      100%     { opacity: 0; }
    }
    @keyframes ${safeId}-loop-trace {
      0%, 24%  { stroke-dashoffset: 100; opacity: 1; }
      44%, 94% { stroke-dashoffset: 0; opacity: 1; }
      100%     { stroke-dashoffset: 0; opacity: 0; }
    }
    .${safeId}-ambient-glow {
      stroke: ${glow}; stroke-width: 1.4; fill: none; stroke-linecap: round;
      stroke-dasharray: 16 84; stroke-dashoffset: 0; opacity: 1;
      animation: ${safeId}-ambient 1600ms linear infinite;
      filter: drop-shadow(0 0 1.2px ${glow});
    }
    @keyframes ${safeId}-ambient {
      from { stroke-dashoffset: 0; }
      to   { stroke-dashoffset: 100; }
    }
    .${safeId}-flow-packet-rect { fill: ${tone}; stroke: ${glow}; stroke-width: 0.35; }
    .${safeId}-flow-packet-text {
      fill: ${glow};
      font-family: var(--app-font-mono, 'JetBrains Mono', Menlo, monospace);
      font-size: 2.1px;
      font-weight: 600;
      letter-spacing: 0.1px;
      text-anchor: middle;
      dominant-baseline: central;
    }
    .${safeId}-flow-die {
      animation: ${safeId}-flow-die-pulse ${FLOW_STAGGER_MS}ms ease-out ${FLOW_FIRST_ARRIVAL_MS}ms infinite;
    }
    @keyframes ${safeId}-flow-die-pulse {
      0%        { fill: ${cut}; transform: scale(1); }
      18%       { fill: ${glow}; transform: scale(1.06); }
      45%, 100% { fill: ${cut}; transform: scale(1); }
    }
    .${safeId}-flow-lock { fill: none; stroke: ${glow}; stroke-width: 0.45; opacity: 0; }
    .${safeId}-flow-lock-body { fill: ${glow}; stroke: none; }
    .${safeId}-flow-lock-group {
      opacity: 0;
      animation: ${safeId}-flow-lock-flash ${FLOW_STAGGER_MS}ms ease-out ${FLOW_FIRST_ARRIVAL_MS}ms infinite;
    }
    @keyframes ${safeId}-flow-lock-flash {
      0%        { opacity: 0; }
      18%       { opacity: 1; }
      55%       { opacity: 0; }
      100%      { opacity: 0; }
    }
    ${flowPacketKeyframes}
    @media (prefers-reduced-motion: reduce) {
      .${safeId}-handshake-glow,
      .${safeId}-handshake-die,
      .${safeId}-handshake-frame,
      .${safeId}-poweron-frame,
      .${safeId}-poweron-die,
      .${safeId}-poweron-pin,
      .${safeId}-poweron-trace,
      .${safeId}-ambient-glow,
      .${safeId}-flow-die,
      .${safeId}-flow-lock-group,
      [class*="${safeId}-flow-packet-"],
      [class*="${safeId}-flow-text-"],
      [class*="${safeId}-flow-lit-"] {
        animation: none !important;
      }
      .${safeId}-handshake-glow,
      .${safeId}-ambient-glow,
      .${safeId}-flow-packet-group,
      .${safeId}-flow-lock-group,
      [class*="${safeId}-flow-text-s1-"],
      [class*="${safeId}-flow-text-s2-"],
      [class*="${safeId}-flow-text-s3-"],
      [class*="${safeId}-flow-text-cipher-"] {
        opacity: 0 !important;
      }
    }
  `;

  return (
    <svg viewBox="0 0 96 96" className={className ?? "w-full h-full"} xmlns="http://www.w3.org/2000/svg">
      <style>{css}</style>
      <defs>
        <clipPath id={`${safeId}-clip`}>
          <path d={SHIELD_PATH} />
        </clipPath>
      </defs>
      {variant === "outline" ? (
        <path d={SHIELD_PATH} fill="none" stroke={tone} strokeWidth={1.6} strokeLinejoin="round" />
      ) : (
        <path d={SHIELD_PATH} fill={tone} />
      )}
      <g clipPath={`url(#${safeId}-clip)`}>
        <rect
          x={x0} y={y0} width={w} height={w} rx={2}
          className={
            beat === "handshake" ? `${safeId}-frame ${safeId}-handshake-frame`
            : beat === "power-on" ? `${safeId}-frame ${safeId}-poweron-frame`
            : `${safeId}-frame`
          }
          pathLength={100}
        />
        <rect
          x={cx - 10} y={cy - 10} width={20} height={20} rx={1.2}
          className={
            beat === "handshake" ? `${safeId}-die ${safeId}-handshake-die`
            : beat === "power-on" ? `${safeId}-die ${safeId}-poweron-die`
            : beat === "data-flow" ? `${safeId}-die ${safeId}-flow-die`
            : `${safeId}-die`
          }
        />
        {beat === "data-flow" && (
          <g className={`${safeId}-flow-lock-group`}>
            <rect
              x={cx - 1.6} y={cy - 0.2} width={3.2} height={2.6} rx={0.4}
              className={`${safeId}-flow-lock-body`}
            />
            <path
              d={`M ${cx - 1} ${cy - 0.2} V ${cy - 1.2} A 1 1 0 0 1 ${cx + 1} ${cy - 1.2} V ${cy - 0.2}`}
              className={`${safeId}-flow-lock`}
            />
          </g>
        )}
        {PIN_STUBS.map((p, i) => (
          <rect
            key={`pin-${i}`}
            x={p.x} y={p.y} width={p.w} height={p.h}
            className={beat === "power-on" ? `${safeId}-pin ${safeId}-poweron-pin` : `${safeId}-pin`}
            style={beat === "power-on" ? { animationDelay: `${1000 + i * 8}ms` } : undefined}
          />
        ))}
        {TRACES.map((t, i) => {
          const baseClass = `${safeId}-trace-base`;
          const extraClass = beat === "power-on" ? ` ${safeId}-poweron-trace` : "";
          const delay = beat === "power-on" ? 1100 + t.ringIndex * 120 + (i % 4) * 30 : 0;
          return (
            <path
              key={`base-${i}`}
              d={t.d}
              className={`${baseClass}${extraClass}`}
              pathLength={100}
              style={beat === "power-on" ? { animationDelay: `${delay}ms` } : undefined}
            />
          );
        })}
        {beat === "handshake" && TRACES.map((t, i) => (
          <path
            key={`glow-${i}`}
            d={t.d}
            className={`${safeId}-glow ${safeId}-handshake-glow`}
            pathLength={100}
            style={{ animationDelay: `${(t.ringIndex * 60) % 240}ms` }}
          />
        ))}
        {beat === "ambient" && TRACES.map((t, i) => (
          <path
            key={`amb-${i}`}
            d={t.d}
            className={`${safeId}-ambient-glow`}
            pathLength={100}
            style={{ animationDelay: `${(i * 90) % 1800}ms` }}
          />
        ))}
        {beat === "data-flow" && TRACES.map((t, i) => (
          <path
            key={`flow-base-${i}`}
            d={t.d}
            className={`${safeId}-ambient-glow`}
            pathLength={100}
            style={{ animationDelay: `${(i * 90) % 1800}ms` }}
          />
        ))}
        {beat === "data-flow" && FLOW_PACKETS.map((_p, i) => {
          const label = labels[packetLabelIdx[i] ?? 0] ?? "";
          const scramble = FLOW_SCRAMBLE_GLYPHS[i % FLOW_SCRAMBLE_GLYPHS.length];
          return (
            <g
              key={`pkt-${i}`}
              ref={(el) => { packetRefs.current[i] = el; }}
              className={`${safeId}-flow-packet-group ${safeId}-flow-packet-${i}`}
            >
              <rect
                x={-3.5} y={-1.6} width={7} height={3.2} rx={0.8}
                className={`${safeId}-flow-packet-rect ${safeId}-flow-lit-${i}`}
              />
              <text x={0} y={0.1} className={`${safeId}-flow-packet-text ${safeId}-flow-text-plain-${i}`}>{label}</text>
              <text x={0} y={0.1} className={`${safeId}-flow-packet-text ${safeId}-flow-text-s1-${i}`}>{scramble[0]}</text>
              <text x={0} y={0.1} className={`${safeId}-flow-packet-text ${safeId}-flow-text-s2-${i}`}>{scramble[1]}</text>
              <text x={0} y={0.1} className={`${safeId}-flow-packet-text ${safeId}-flow-text-s3-${i}`}>{scramble[2]}</text>
              <text x={0} y={0.1} className={`${safeId}-flow-packet-text ${safeId}-flow-text-cipher-${i}`}>{FLOW_CIPHER_GLYPH}</text>
            </g>
          );
        })}
      </g>
    </svg>
  );
}

export function ShieldMark({ className, tone, cut, variant }: { className?: string; tone?: string; cut?: string; variant?: "filled" | "outline" }) {
  return <AnimatedShield beat="static" className={className} tone={tone} cut={cut} variant={variant} />;
}
